import React, { useState, useEffect } from 'react';
import { useCMS, PublicPageType } from '../../context/CMSContext';
import { Project } from '../../types/cms';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import { BrandLogo } from '../common/BrandLogo';
import { INITIAL_CMS_DATA } from '../../data/initialData';
import { getComputedTextStyle } from '../../utils/typographyHelper';

export const Navbar: React.FC = () => {
  const { 
    data, 
    activePublicPage,
    setActivePublicPage,
    setSelectedProjectTab,
    selectedProject,
    setSelectedProject, 
    setIsProjectDetailModalOpen,
    setIsProspectusModalOpen, 
    setIsStoryModalOpen,
    setIsConnectModalOpen,
    setIsFaqsModalOpen,
    isInitialLoading
  } = useCMS();

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [mobileOpenGroup, setMobileOpenGroup] = useState<string | null>(null);
  const headerRef = React.useRef<HTMLElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  // Pages that have a light background at the top and should show the solid dark navbar
  const isLightPage = ['journal-detail', 'leadership-detail', 'privacy', 'terms'].includes(activePublicPage);
  const showDarkNavbar = isScrolled || isLightPage;

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
      setIsScrolled(scrollPos > 15);
    };

    // Run on mount and route change
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activePublicPage]);

  useEffect(() => {
    let rAF: number | null = null;
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
      if (rAF !== null) cancelAnimationFrame(rAF);
      rAF = window.requestAnimationFrame(() => {
        if (headerRef.current) {
          const h = headerRef.current.offsetHeight;
          if (h > 0) {
            const currentH = document.documentElement.style.getPropertyValue('--navbar-height');
            if (currentH !== `${h}px`) {
              document.documentElement.style.setProperty('--navbar-height', `${h}px`);
            }
          }
        }
      });
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      if (rAF !== null) cancelAnimationFrame(rAF);
    };
  }, []);

  const resolveTarget = (rawUrl: string): { page?: PublicPageType; sectionId?: string; external?: string; projectId?: string } => {
    if (!rawUrl) return { page: 'home' };
    const clean = rawUrl.trim();
    if (clean.startsWith('http://') || clean.startsWith('https://')) {
      return { external: clean };
    }
    if (clean.startsWith('#')) {
      const section = clean.replace('#', '');
      return { page: 'home', sectionId: section };
    }

    // Support project id syntax: project-detail:id, project:id, or project-detail?id=id
    if (clean.startsWith('project-detail:') || clean.startsWith('project:')) {
      const parts = clean.split(':');
      return { page: 'project-detail', projectId: parts[1]?.trim() };
    }
    if (clean.startsWith('project-detail?id=')) {
      const id = clean.split('id=')[1]?.trim();
      return { page: 'project-detail', projectId: id };
    }

    const knownPages: PublicPageType[] = [
      'home', 'projects', 'project-detail', 'mumbai-3', 'about',
      'philosophy', 'why-oltraya', 'investor-group', 'leadership',
      'journal', 'journal-detail', 'faqs', 'connect', 'privacy', 'terms'
    ];
    if (knownPages.includes(clean as PublicPageType)) {
      return { page: clean as PublicPageType };
    }
    return { page: 'home' };
  };

  const navigateToPage = (
    page: PublicPageType,
    sectionId?: string,
    tab?: 'ALL PROJECTS' | 'CURRENT' | 'UPCOMING' | 'COMPLETED'
  ) => {
    setMobileMenuOpen(false);
    setOpenDropdownId(null);

    if (tab) {
      setSelectedProjectTab(tab);
    }

    if (page !== activePublicPage) {
      setActivePublicPage(page);
    }

    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavClick = (url: string, label?: string, projectId?: string) => {
    const { page, sectionId, external, projectId: parsedProjectId } = resolveTarget(url);
    if (external) {
      window.open(external, '_blank', 'noopener,noreferrer');
      return;
    }

    const targetProjectId = projectId || parsedProjectId;

    // If navigating to a project detail page (e.g. "Oltraya Verde" or other projects)
    if (page === 'project-detail' || url.includes('project-detail') || url.includes('project:')) {
      const allProjects = data.projectsSection?.projects || [];
      let matchedProject: Project | undefined;

      if (targetProjectId) {
        matchedProject = allProjects.find(
          (p) => p.id === targetProjectId || p.id.toLowerCase() === targetProjectId.toLowerCase()
        );
      }

      if (!matchedProject && label) {
        const cleanLabel = label.toLowerCase().trim();
        matchedProject = allProjects.find(
          (p) =>
            p.title.toLowerCase() === cleanLabel ||
            p.id.toLowerCase() === cleanLabel ||
            cleanLabel.includes(p.title.toLowerCase()) ||
            p.title.toLowerCase().includes(cleanLabel) ||
            cleanLabel.includes(p.id.toLowerCase())
        );
      }

      // Fallback if no specific project matched: default to the first active project (e.g. Oltraya Verde)
      if (!matchedProject && allProjects.length > 0) {
        matchedProject = allProjects.find((p) => p.isEnabled !== false) || allProjects[0];
      }

      if (matchedProject) {
        setSelectedProject(matchedProject);
      }
    }

    if (page) {
      navigateToPage(page, sectionId);
    }
  };

  const navItems = data.navigation?.items && data.navigation.items.length > 0
    ? data.navigation.items
    : INITIAL_CMS_DATA.navigation.items;

  const isItemActive = (item: any) => {
    const { page } = resolveTarget(item.url);
    if (page && page === activePublicPage) return true;
    if (item.subItems) {
      return item.subItems.some((sub: any) => {
        const subTarget = resolveTarget(sub.url);
        return subTarget.page && subTarget.page === activePublicPage;
      });
    }
    return false;
  };

  const navTypographyStyle = getComputedTextStyle(
    data.typography?.navLinks,
    data.navigation?.style,
    {
      fontSize: 12,
      fontWeight: '500',
      fontColor: '#e2ded6',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      fontFamily: 'sans'
    }
  );

  const navBgColor = data.siteSettings?.headerBgColor || (data.navigation as any)?.backgroundColor || '#0a122a';

  return (
    <>
      <div 
        ref={sentinelRef} 
        id="navbar-scroll-sentinel" 
        className="absolute top-0 left-0 w-full h-[12px] pointer-events-none opacity-0 -z-50" 
        aria-hidden="true" 
      />
      <header
        ref={headerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: showDarkNavbar ? `${navBgColor}f9` : undefined,
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        className={`w-full transition-all duration-300 ${
          showDarkNavbar
            ? 'backdrop-blur-xl border-b border-white/10 shadow-2xl py-3 md:py-3.5'
            : 'mobile-nav-bg md:bg-transparent backdrop-blur-xl md:backdrop-blur-none border-b border-white/10 md:border-b-transparent py-3.5 md:py-5'
        }`}
      >
        <style dangerouslySetInnerHTML={{__html: `
          @media (max-width: 767px) {
            .mobile-nav-bg { background-color: ${navBgColor}f9 !important; }
          }
        `}} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between">
          {/* Brand Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              navigateToPage('home');
            }}
            className="group block shrink-0"
          >
            <BrandLogo />
          </a>

          {/* Desktop Navigation Links Dynamically Synced from Admin - Aligned to Top Right */}
          <nav 
            className="hidden md:flex items-center space-x-6 lg:space-x-8 uppercase ml-auto"
            style={navTypographyStyle}
          >
            {navItems.map((item) => {
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const active = isItemActive(item);

              if (hasSubItems) {
                return (
                  <div
                    key={item.id}
                    className="relative group py-2"
                    onMouseEnter={() => setOpenDropdownId(item.id)}
                    onMouseLeave={() => setOpenDropdownId(null)}
                  >
                    <button
                      onClick={() => handleNavClick(item.url, item.label)}
                      className={`flex items-center space-x-1.5 hover:text-[#c5a869] transition-colors py-1 cursor-pointer ${
                        active ? 'text-[#c5a869]' : 'text-[#e2ded6]'
                      }`}
                      style={{ color: active ? '#c5a869' : undefined }}
                    >
                      <span>{item.label}</span>
                      <ChevronDown className="w-3 h-3 opacity-75 group-hover:text-[#c5a869] transition-transform group-hover:rotate-180 duration-200" />
                    </button>

                    {/* Dropdown Menu */}
                    <div 
                      style={{ backgroundColor: '#081022f5' }}
                      className="absolute top-full right-0 hidden group-hover:block w-max min-w-[200px] border border-white/10 rounded-none shadow-2xl py-2 px-1.5 z-50 backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-150"
                    >
                      {item.subItems?.map((sub) => {
                        const subTarget = resolveTarget(sub.url);
                        const isProject = subTarget.page === 'project-detail' || sub.url.includes('project-detail') || sub.url.includes('project:');
                        const isSubActive = isProject
                          ? activePublicPage === 'project-detail' && (!sub.label || selectedProject?.title.toLowerCase() === sub.label.toLowerCase() || selectedProject?.id === (sub as any).projectId)
                          : subTarget.page === activePublicPage;

                        return (
                          <button
                            key={sub.id}
                            onClick={() => handleNavClick(sub.url, sub.label, (sub as any).projectId)}
                            className="w-full text-left px-3.5 py-2 hover:bg-white/5 transition-colors rounded-none flex items-center justify-between group/sub cursor-pointer"
                          >
                            <span 
                              style={{ fontFamily: navTypographyStyle.fontFamily }}
                              className={`text-xs font-medium uppercase tracking-widest transition-colors block ${
                                isSubActive ? 'text-[#a88345]' : 'text-[#d6d4ce] group-hover/sub:text-[#a88345]'
                              }`}
                            >
                              {sub.label}
                            </span>
                            <ArrowUpRight className="w-3.5 h-3.5 text-white/20 group-hover/sub:text-[#a88345] transition-colors shrink-0 ml-3" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.url, item.label)}
                  className={`hover:text-[#a88345] transition-colors py-2 cursor-pointer ${
                    active ? 'text-[#a88345]' : ''
                  }`}
                  style={{ color: active ? '#a88345' : 'inherit' }}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center space-x-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#f7f2e8] p-2 hover:text-[#a88345] transition-colors cursor-pointer flex items-center justify-center focus:outline-none"
              aria-label="Toggle Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div
            style={{ 
              backgroundColor: `${navBgColor}f9`,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}
            className="md:hidden border-b border-white/10 px-6 py-6 space-y-4 max-h-[calc(100vh-80px)] overflow-y-auto animate-in fade-in slide-in-from-top-4 duration-200 shadow-2xl"
          >
            <div 
              style={{
                fontFamily: navTypographyStyle.fontFamily,
                letterSpacing: navTypographyStyle.letterSpacing || '0.18em',
                textTransform: navTypographyStyle.textTransform || 'uppercase'
              }}
              className="flex flex-col space-y-3 text-xs text-[#e0deda]"
            >
              {navItems.map((item) => {
                const hasSubItems = item.subItems && item.subItems.length > 0;
                const isGroupOpen = mobileOpenGroup === item.id;
                const active = isItemActive(item);

                if (hasSubItems) {
                  return (
                    <div key={item.id} className="border-b border-white/10 pb-2">
                      <button
                        onClick={() => setMobileOpenGroup(isGroupOpen ? null : item.id)}
                        className={`text-left py-2 font-medium hover:text-[#a88345] w-full flex items-center justify-between cursor-pointer transition-colors ${
                          active ? 'text-[#c5a869]' : ''
                        }`}
                      >
                        <span>{item.label}</span>
                        <span className="text-sm text-[#a88345] font-mono">
                          {isGroupOpen ? '−' : '+'}
                        </span>
                      </button>

                      {isGroupOpen && (
                        <div className="pl-3 py-1 space-y-2 border-l border-[#a88345]/40 ml-2 mt-1">
                          {item.subItems?.map((sub) => (
                            <button
                              key={sub.id}
                              onClick={() => handleNavClick(sub.url, sub.label, (sub as any).projectId)}
                              style={{ fontFamily: navTypographyStyle.fontFamily }}
                              className="text-left py-2 block w-full text-xs text-[#d6d4ce] hover:text-[#a88345] tracking-wider cursor-pointer transition-colors"
                            >
                              • {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.url, item.label)}
                    className={`text-left py-2 border-b border-white/10 hover:text-[#a88345] cursor-pointer transition-colors ${
                      active ? 'text-[#a88345]' : ''
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}

              <div className="pt-3">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setIsProspectusModalOpen(true);
                  }}
                  style={{
                    fontFamily: navTypographyStyle.fontFamily,
                    letterSpacing: '0.18em'
                  }}
                  className="w-full py-3 bg-[#a88345] text-[#0a122a] font-bold text-xs uppercase tracking-widest text-center shadow-md cursor-pointer rounded-none hover:bg-[#b89552] transition-colors"
                >
                  Request Prospectus
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};
